import {
  LAYOUT_CONFLICT_ERROR_NAME,
  type VisualizerInitData,
  type VisualizerNotice,
} from "../loader";

import { type Notification } from "./Notifications";

/**
 * The notice as the notification strip renders it. The notice's `name` becomes
 * the notification's id: the key the strip renders each banner under, and what
 * dismissing matches on, so notices raised together need distinct names.
 */
export function notificationFor(notice: VisualizerNotice): Notification {
  return {
    id: notice.name,
    title: notice.title,
    message: notice.message,
    severity: notice.severity,
  };
}

/**
 * `notifications`, followed by every notification of `raised` whose id is not
 * already among them.
 *
 * The workspace raises banners from effects that run again whenever their
 * inputs change, so the same banner arrives repeatedly; matching on id keeps it
 * to one. Dismissing drops a notification from the list, so a banner the user
 * closed reappears the next time its effect runs. When nothing is new the same
 * array comes back, so a state setter given the result sees no change.
 */
export function withNewNotifications(
  notifications: Notification[],
  raised: Notification[],
): Notification[] {
  const shownIds = new Set(
    notifications.map((notification) => notification.id),
  );
  const additions = raised.filter(
    (notification) => !shownIds.has(notification.id),
  );
  return additions.length === 0
    ? notifications
    : [...notifications, ...additions];
}

/**
 * Seed the workspace's notifications from the loader: every banner it raised
 * while resolving the route, plus the layout error it recovered from. The
 * layout-conflict error is left out because its own modal handles it.
 */
export function initialNotifications(
  initData: VisualizerInitData,
): Notification[] {
  const notifications = initData.notices.map(notificationFor);

  const configSource = initData.configSource;
  if (
    configSource?.type === "recoverable-error" &&
    configSource.value.name !== LAYOUT_CONFLICT_ERROR_NAME
  ) {
    notifications.push({
      id: configSource.value.name,
      title: configSource.value.title,
      message: configSource.value.message,
      severity: "error",
    });
  }

  return notifications;
}
